import { isSymbol } from 'lodash-es'
import React, { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react'
import { TypedUseSelectorHook, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { z } from 'zod'

import { entityGenerator } from '@/lib/entityGenerator'
import { useIsMobile } from '@/lib/mui/useIsMediaQuery'
import {
	AuthActions,
	IAction,
	isRequestTypeAction,
	LiveUserLocationsActions,
	RequestTypeMapping,
} from '@/redux/actions'
import { createLoadingSelector } from '@/redux/reducers/loading.reducer'
import { useDispatch } from '@/redux/stores'
import { ApplicationState } from '@/redux/stores/types'
import { EmptyVoidCallback, VoidCallback } from '@/travelsuit/callback'
import { AuthUser } from '@/types/auth'
import { ArrayOr, UserId } from '@/types/common'
import { CompanyPreferenceType } from '@/types/company'
import { User } from '@/types/user'
import { ObFeeMockValue } from '@/types/userNew'

import { forceArray } from '../array-utils'

export const useStoreValue: TypedUseSelectorHook<ApplicationState> = useSelector

export function selectCurrentUser({ auth }: ApplicationState) {
	return auth.get('internalUser') as User
}

export function selectAuthUser({ auth }: ApplicationState) {
	return auth.get('user') as AuthUser
}

export function useUser() {
	return useStoreValue(selectCurrentUser)
}

export function useAuthUser() {
	return useStoreValue(selectAuthUser)
}

export function useAdminUsers() {
	return useStoreValue(({ adminUsers }) => adminUsers)
}

export function usePreference(key: CompanyPreferenceType) {
	return useStoreValue(({ myCompany }) => myCompany?.preferences.find((p) => p.preference_type === key))
}

export function useLoadingSelector(
	key: string | Pick<RequestTypeMapping, 'ORIGINAL'>,
	...keys: Array<string | Pick<RequestTypeMapping, 'ORIGINAL'>>
) {
	const stringKeys: string[] = [key, ...keys].map((key) => (isRequestTypeAction(key) ? key.ORIGINAL : (key as string)))
	return useSelector((state: ApplicationState) => createLoadingSelector(stringKeys)(state.loading))
}

export function useLoadingSelectorTrueOnInit(...args: Parameters<typeof useLoadingSelector>) {
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		setIsLoading(false)
	}, [])

	return useLoadingSelector(...args) || isLoading
}

export function useLoadingSelectorOnce(
	key: string | Pick<RequestTypeMapping, 'ORIGINAL'>,
	...keys: Array<string | Pick<RequestTypeMapping, 'ORIGINAL'>>
) {
	const loading = useLoadingSelector(key, ...keys)
	const [afterLoad, setAfterLoad] = React.useState(false)

	React.useEffect(() => {
		if (!loading) {
			setAfterLoad(true)
		}
	}, [loading])

	return !afterLoad
}

export function useLiveLocationMap() {
	const userLocations = useStoreValue((state) => state.liveUserLocations.userLocations)
	const dispatch = useDispatch()

	React.useEffect(() => {
		dispatch(LiveUserLocationsActions.getLiveUserLocations())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return userLocations
}

/** Keeps a reference of the value at call time. This is useful when you want to access previous values inside
 * an effect hook.
 *
 * Example:
 * ```typescript
 * const counter = React.useState(0)
 * const previousCounter = usePreviousValue(counter)
 *
 * React.useEffect(() => console.info({ counter, previousCounter }), [counter])
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePreviousValue<T = any>(value: T): T {
	const ref = React.useRef<T>(value)
	React.useEffect(() => {
		ref.current = value
	}, [value])

	return ref.current!
}

export interface APIHookOptions {
	refresh: boolean
	onLoad?: EmptyVoidCallback
	onError?: VoidCallback<unknown>
}

const withDefaultAPIHookOptions = entityGenerator<APIHookOptions>({
	refresh: false,
})

export interface APIHookCreatorConfig<O, T> {
	loadingSelectors: ArrayOr<string | RequestTypeMapping>
	storeMapper(state: ApplicationState, params: O, options: APIHookOptions): T
	loadAction(params: O, options: APIHookOptions): IAction
	mapParamsToHookDeps?(params: O): unknown[]
}

interface LoadOnceAPIHookCreatorConfig<O, T> extends APIHookCreatorConfig<O, T> {
	isEmpty(state: T): boolean
}

interface PollAPIHookCreatorConfig<O, T> extends APIHookCreatorConfig<O, T> {
	pollInterval?: number
}

type APIHookOptionsFunction<O, T> = (
	params: O,
	options?: Partial<APIHookOptions>,
) => { dispatchLoadAction: () => void; loading: boolean; options: APIHookOptions; data: T }

type APIHookFunction<O, T> = (params: O, options?: Partial<APIHookOptions>, dependencies?: unknown[]) => T

type PollAPIHookFunction<O, T> = (
	params: O,
	shouldContinuePolling: (value: T | undefined) => boolean,
	options?: Partial<APIHookOptions>,
) => T

function createAPIOptionsHook<O, T>(config: APIHookCreatorConfig<O, T>): APIHookOptionsFunction<O, T> {
	return (params, optionsOverride) => {
		const options = withDefaultAPIHookOptions(optionsOverride)
		const data = useStoreValue((state) => config.storeMapper(state, params, options))
		const selectors = forceArray(config.loadingSelectors)
		const loading = useLoadingSelector(selectors[0], ...selectors.slice(1))
		const dispatch = useDispatch()

		const dispatchLoadAction = () => dispatch(config.loadAction(params, options))

		return {
			options,
			dispatchLoadAction,
			loading,
			data,
		}
	}
}

export function createAPIHook<O, T>(config: APIHookCreatorConfig<O, T>): APIHookFunction<O, T> {
	const useAPIOptions = createAPIOptionsHook(config)

	return (params, optionsOverride, dependencies = []) => {
		const { dispatchLoadAction, loading, options, data } = useAPIOptions(params, optionsOverride)

		React.useEffect(() => {
			if (options.refresh && !loading) {
				dispatchLoadAction()
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [options.refresh, ...(config.mapParamsToHookDeps?.(params) ?? []), ...dependencies])

		return data
	}
}

export function createApiHookWithLoadingState<O, T>(
	config: APIHookCreatorConfig<O, T>,
): (params: O, options?: Partial<APIHookOptions>) => { data: T; isLoading: boolean } {
	const useApiHook = createAPIHook(config)

	const [loadingSelector, ...restSelectors] = forceArray(config.loadingSelectors)

	return function useAPIHookWithLoadingState(params: O, optionsOverride?: Partial<APIHookOptions>) {
		const loading = useLoadingSelector(loadingSelector, ...restSelectors)
		const [shouldRefresh, setShouldRefresh] = React.useState(optionsOverride?.refresh !== false && !loading)

		const data = useApiHook(params, {
			refresh: shouldRefresh,
			onLoad: () => {
				setShouldRefresh(false)
				optionsOverride?.onLoad?.()
			},
			onError: (error) => {
				setShouldRefresh(false)
				optionsOverride?.onError?.(error)
			},
		})

		return {
			data,
			isLoading: loading || shouldRefresh,
		}
	}
}

export function createLoadOnceAPIHook<O, T>(config: LoadOnceAPIHookCreatorConfig<O, T>): APIHookFunction<O, T> {
	const useAPIOptions = createAPIOptionsHook(config)

	return (params, optionsOverride) => {
		const { dispatchLoadAction, loading, data } = useAPIOptions(params, optionsOverride)
		const isDataEmpty = config.isEmpty(data)

		React.useEffect(() => {
			if (isDataEmpty && !loading) {
				dispatchLoadAction()
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isDataEmpty])

		return data
	}
}

/** @deprecated */
export function createPollAPIHook<O, T>(config: PollAPIHookCreatorConfig<O, T>): PollAPIHookFunction<O, T> {
	const useAPIOptions = createAPIOptionsHook(config)

	return (params, shouldContinuePolling, optionsOverride) => {
		const { dispatchLoadAction, loading, data } = useAPIOptions(params, optionsOverride)

		React.useEffect(() => {
			if (!shouldContinuePolling(data) || loading) {
				return
			}

			const timeoutId = setTimeout(dispatchLoadAction, config.pollInterval)

			return () => {
				clearTimeout(timeoutId)
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [loading, data, shouldContinuePolling])

		return data
	}
}

export type ComplexStateUpdater<T> = (
	partialState: Partial<T> | ((prevState: T) => Partial<T>) | typeof useComplexState.RESET,
) => void

export function useComplexState<T>(initial: T): [T, ComplexStateUpdater<T>] {
	const [state, setState] = useReducer<T, [Partial<T> | ((prevState: T) => Partial<T>) | typeof useComplexState.RESET]>(
		(prevState, partialState) =>
			isSymbol(partialState)
				? initial
				: {
						...prevState,
						...(typeof partialState === 'function' ? partialState(prevState) : partialState),
					},
		initial,
	)

	return [state, setState]
}

useComplexState.RESET = z.symbol().brand<'UseComplexStateReset'>().parse(Symbol())

/**
 * Allow to inherit and re-use the ref coming from props and useImperativeHandle
 * is an overkill. Like when both the parent component and the current component
 * want to have a ref just the be able to call `.focus()`
 *
 * Example:
 * ```ts
 * function TextField({ inputRef: propsRef, ...rest }) {
 *   const inputRef = useCommonRef(propsRef)
 *
 *   useEffect(() => {
 *     inputRef.current?.focus()
 *   })
 *
 *   return <input ref={inputRef} type="text" {...rest} />
 * }
 * ```
 */
export function useSynchronizedRef<T>(
	refToSync: React.RefObject<T | null> | undefined,
	initialValue: T | null = null,
): React.RefObject<T | null> {
	const myRef = useRef<T>(initialValue)

	useLayoutEffect(() => {
		const updateRef = () => {
			if (refToSync) {
				// eslint-disable-next-line no-param-reassign
				;(refToSync as React.MutableRefObject<T | null>).current = myRef.current
			}
		}
		updateRef()
		return updateRef
	})

	return myRef
}

export type UseOnClickOutsideOptions = {
	/** Listen for clicks outside of this container */
	containerRef: React.RefObject<HTMLElement | null>
	onClickOutside: (e: MouseEvent | TouchEvent) => void
	/** Set this to `false` when your popup is closed to save on DOM listeners */
	shouldListen?: boolean
}

export function useOnClickOutsideOnDesktop({ containerRef, onClickOutside, shouldListen }: UseOnClickOutsideOptions) {
	const isMobile = useIsMobile()

	useEffect(() => {
		// turning off on mobile - it doesn't work well when using overlapping modals
		if (isMobile || !containerRef.current || shouldListen === false) {
			return
		}

		const listener = (evt: MouseEvent | TouchEvent) => {
			if (evt.target && containerRef.current && !evt.composedPath().includes(containerRef.current)) {
				onClickOutside(evt)
			}
		}

		document.addEventListener('click', listener)
		document.addEventListener('touchstart', listener)

		return () => {
			document.removeEventListener('click', listener)
			document.removeEventListener('touchstart', listener)
		}
	}, [containerRef, isMobile, onClickOutside, shouldListen])
}

export type MutableValue<T> = { readonly current: T }
/**
 * When you want to use a mutable container for its speed,
 * but still need to update the UI after mutating the value.
 * Works like `useRef()`, but opposite.
 * The container reference changes on the updater function call.
 * ```tsx
 * const [expandedMap, updateExpandedMap] = useMutableValue(new Map<string, boolean>())
 * const toggleRow = (rowId: string) => {
 *   expandedMap.current.set(rowId, !expandedMap.current.get(rowId))
 *   updateExpandedMap()
 *   // or the preferred single-line variant
 *   updateExpandedMap((em) => em.set(rowId, !em.get(rowId))
 * }
 * return <Row expanded={expandedMap.current.get(rowId)} onClick={() => toggleRow(rowId)} />
 * ```
 */
export function useMutableValue<T>(initialValue: T | (() => T)) {
	const [value] = useState(initialValue)
	const [container, setContainer] = useState<MutableValue<T>>({ current: value })
	const updateContainer = useCallback(
		(mutation?: (current: T) => void) => {
			mutation?.(value)
			setContainer({ current: value })
		},
		[value],
	)
	return [container, updateContainer] as const
}

export function useForceRefresh(): () => void {
	const forceRefresh = useMutableValue(0)[1]
	return useCallback(() => forceRefresh(), [forceRefresh])
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CB<A = unknown> = (value?: A) => any

/**
 * In the newer type-fest there array helpers to cut off resCb and errCb from Parameters,
 * but we can't import is due to the TS requirements. So, for now it would be manual p1, p2...
 */
export interface AsyncDispatch {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	<R>(action: (resCb?: CB<R>, errCb?: CB) => any): Promise<R>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	<P1, R>(action: (p1: P1, resCb?: CB<R>, errCb?: CB) => any, p1: P1): Promise<R>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	<P1, P2, R>(action: (p1: P1, p2: P2, resCb?: CB<R>, errCb?: CB) => any, p1: P1, p2: P2): Promise<R>
	<P1, P2, P3, R>(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		action: (p1: P1, p2: P2, p3: P3, resCb?: CB<R>, errCb?: CB) => any,
		p1: P1,
		p2: P2,
		p3: P3,
	): Promise<R>
	<P1, P2, P3, P4, R>(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		action: (p1: P1, p2: P2, p3: P3, p4: P4, resCb?: CB<R>, errCb?: CB) => any,
		p1: P1,
		p2: P2,
		p3: P3,
		p4: P4,
	): Promise<R>
}

export function promisifyDispatch(dispatch: Dispatch<IAction>): AsyncDispatch {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return <A extends (...args: any[]) => any>(action: A, ...args: Parameters<A>) =>
		new Promise((resolve, reject) => {
			args.push(resolve, reject)
			dispatch(action(...args))
		})
}

export function useAsyncDispatch() {
	const dispatch = useDispatch()
	return useMemo(() => promisifyDispatch(dispatch), [dispatch])
}

/** The return value is stable */
export function useIsMounted() {
	const ref = useRef({ value: true })
	useEffect(
		() => () => {
			ref.current.value = false
		},
		[],
	)
	return ref.current
}

export function useResizeObserver(
	ref: RefObject<HTMLElement | null>,
	onResize: (contentRect: DOMRectReadOnly) => void,
	deps?: unknown[],
) {
	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => onResize(entries[0].contentRect))

		if (ref.current) {
			resizeObserver.observe(ref.current)
		}

		return () => resizeObserver.disconnect()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps)
}

export function useObFeeMock(userId: UserId, currentObFeeMock: ObFeeMockValue | null | undefined) {
	const dispatch = useDispatch()

	const obFeeMock = currentObFeeMock ?? null

	const setObFeeMock = useCallback(
		(value: ObFeeMockValue) => {
			dispatch(AuthActions.setIsObFeeMockEnabled(userId, obFeeMock === value ? null : value))
		},
		[dispatch, userId, obFeeMock],
	)

	return [obFeeMock, setObFeeMock] as const
}
